/**
 * The list item a card is, or sits in. Usually the card itself; where a list
 * wraps each card in an item of its own — antd's Timeline does — `rowSelector`
 * names that item, and it is the row that is aligned and measured.
 */
export const rowOf = (card: HTMLElement, rowSelector?: string): HTMLElement =>
    (rowSelector && card.closest<HTMLElement>(rowSelector)) || card;

/** the nearest ancestor that scrolls — on a page, its own pane under the top bar */
export const scrollPaneOf = (node: HTMLElement): HTMLElement | null => {
    for (let el = node.parentElement; el; el = el.parentElement) {
        if (isScrollArea(el)) return el;
    }
    return null;
};

/** whether an element is a scroll area — by its style, whether or not it has overflowed yet */
export const isScrollArea = (el: Element): boolean => {
    const { overflowY } = window.getComputedStyle(el);
    return overflowY === 'auto' || overflowY === 'scroll';
};

/** a scroll area with more in it than it shows — size first, as reading style costs */
export const overflows = (el: Element): boolean => el.scrollHeight > el.clientHeight + 1 && isScrollArea(el);

/** whether a scroll area has room to move further that way */
export const canMoveFurther = (el: Element, down: boolean): boolean =>
    (down ? el.scrollTop + el.clientHeight < el.scrollHeight - 1 : el.scrollTop > 0);

/**
 * How far a wheel event asks `el` to scroll, in pixels: a wheel that counts
 * in lines moves 16px a line, one that counts in pages a page of `el`.
 */
export const wheelPixels = (event: WheelEvent, el: HTMLElement): number => {
    const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? el.clientHeight : 1;
    return event.deltaY * unit;
};

/** the scroll area a node is, or else the one it is in */
export const scrollPaneAt = (node: HTMLElement): HTMLElement | null =>
    (isScrollArea(node) ? node : scrollPaneOf(node));

/**
 * How much of a row's height is panels on their way out: usePresence holds a
 * closing panel mounted, at full height, through its exit — so a row that was
 * open a moment ago still measures open. Panels inside one that is closing
 * already count in it.
 */
export const closingHeight = (row: HTMLElement): number => {
    let total = 0;
    row.querySelectorAll<HTMLElement>('[data-state="closing"]').forEach((panel) => {
        const outer = panel.parentElement?.closest('[data-state="closing"]');
        if (outer && row.contains(outer)) return;
        const { marginTop, marginBottom } = window.getComputedStyle(panel);
        total += panel.offsetHeight + (parseFloat(marginTop) || 0) + (parseFloat(marginBottom) || 0);
    });
    return total;
};

/** how tall a row is once whatever is closing in it has gone */
export const settledHeight = (row: HTMLElement): number => row.offsetHeight - closingHeight(row);

/**
 * Calls `onChange` whenever a scroll area's content changes size — which a
 * ResizeObserver on the area itself never sees once the area has a set
 * height: the area keeps its size, and only what is in it grows and shrinks.
 * Watches each box in it (through any wrapper that has none, `display:
 * contents`), and picks up boxes as they come and go. Returns the cleanup.
 */
export const watchContent = (pane: HTMLElement, onChange: () => void): (() => void) => {
    if (typeof ResizeObserver === 'undefined') return () => {};
    const resize = new ResizeObserver(() => onChange());
    const boxes = (parent: Element): Element[] => Array.from(parent.children).flatMap((child) => (
        window.getComputedStyle(child).display === 'contents' ? boxes(child) : [child]
    ));
    let watched: Element[] = [];
    const rewatch = () => {
        const next = boxes(pane);
        watched.filter((box) => !next.includes(box)).forEach((box) => resize.unobserve(box));
        next.filter((box) => !watched.includes(box)).forEach((box) => resize.observe(box));
        watched = next;
    };
    rewatch();
    resize.observe(pane);
    // the area's own children only — not everything inside them
    const children = new MutationObserver(() => { rewatch(); onChange(); });
    children.observe(pane, { childList: true });
    return () => {
        resize.disconnect();
        children.disconnect();
    };
};
